/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { styles } from './themes/PieChartStyles.css';
import {
  getDefaultBorderColor,
  getElementBounds,
  getPieCenter,
  getRadius,
  getRenderedSlices,
  getPieChartInfo,
  getPieSliceGap,
  getSliceInfo
} from './utils';
import { getAvailSpace } from '../utils/PRIVATE_chartUtils/layoutUtils';
import { PieSlice } from './PieSlice';
import { getCenterContext } from './utils';
import { getPieChartNavUtils } from './pieNavUtils';
import { ItemInfo, usePieChartNav } from '../hooks/PRIVATE_usePieChartNav';
import { calculateOffset, useDatatip } from '../hooks/PRIVATE_useDatatip';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { VisTabularDatatip } from '../PRIVATE_VisTabularDatatip';
import type { PieChartProps, PieChartItemData } from './pieChart.types';
import { getColorRamp } from '../utils/UNSAFE_visUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { useRef } from 'preact/hooks';
import { merge } from '#utils/UNSAFE_stringUtils';

type PieChartWithDimensionsProps = Omit<PieChartProps, 'width' | 'height'> & {
  width: number;
  height: number;
  data: PieChartItemData[];
};

const NUM_PIE_SLICE_THRESHOLD = 100;
export function PieChartWithDimensions({
  width,
  height,
  testId,
  innerRadius = 0,
  ...props
}: PieChartWithDimensionsProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const colors = getColorRamp();
  const { totalValue, isLabelOutside } = getPieChartInfo(props.data);
  const sliceInfos = getSliceInfo(props.data, totalValue);

  // default data item gaps is 0.5. TODO: replace 0.5 with pie dataItemGaps/pieSliceGaps
  const pieSliceGap = getPieSliceGap(0.5);
  const hasLargeSeriesCount = props.data.length > NUM_PIE_SLICE_THRESHOLD;
  const { direction } = useUser();
  const testIdProps = useTestId(testId);
  const availSpace = getAvailSpace(width, height);
  const { cx, cy } = getPieCenter(availSpace);
  const radius = getRadius(availSpace, isLabelOutside);
  const ir = innerRadius * radius;
  const renderedSlices = getRenderedSlices(sliceInfos, hasLargeSeriesCount, radius);
  const getNextChartItem = getPieChartNavUtils(renderedSlices, direction === 'rtl');

  const { touchResponse, touchResponseStyle } = useVisTouchResponse({
    type: 'auto',
    supportsTouchDragGestures: false,
    rootRef
  });
  const { activeId, navProps, focusedItemInfo, hoveredItemInfo } = usePieChartNav({
    touchResponse,
    getNextChartItem
  });
  const activeItem = focusedItemInfo.isFocusVisible ? focusedItemInfo : hoveredItemInfo;
  const activeData = activeItem
    ? props.data[renderedSlices[activeItem.itemIndex].index]
    : undefined;

  const defaultDatatip = (activeData?: PieChartItemData, activeItem?: ItemInfo) => {
    if (!activeData) {
      return {
        content: undefined,
        borderColor: undefined
      };
    }
    if (activeData.accessibleLabel) {
      return {
        content: activeData.accessibleLabel,
        borderColor: getDefaultBorderColor(colors, activeItem, activeData)
      };
    }
    return {
      content: (
        <VisTabularDatatip
          tableData={[
            {
              key: 'Value',
              value: activeData.value
            }
          ]}
        />
      ),
      borderColor: getDefaultBorderColor(colors, activeItem, activeData)
    };
  };

  const { content: datatipString, borderColor } =
    activeData && props.datatip
      ? props.datatip({ data: activeData })
      : defaultDatatip(activeData, activeItem);

  const anchor = focusedItemInfo.isFocusVisible ? 'element' : 'pointer';
  const elementBounds = activeItem?.isFocusVisible
    ? getElementBounds(renderedSlices, cx, cy, ir, radius, activeItem)
    : undefined;
  const { datatipContent, datatipProps } = useDatatip({
    content: datatipString,
    offset: calculateOffset(direction === 'rtl', width, elementBounds),
    anchor,
    placement: 'top-start',
    borderColor,
    touchResponse
  });

  datatipProps['aria-describedby'] = merge([
    props['aria-describedby'],
    datatipProps['aria-describedby']
  ]);

  const mergedProps = mergeProps(navProps, datatipProps);
  return (
    <div
      ref={rootRef}
      tabIndex={0}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      aria-activedescendant={activeId}
      role="application"
      {...mergedProps}
      {...testIdProps}
      className={styles.chart}
      style={{
        width,
        height,
        ...touchResponseStyle
      }}>
      {renderedSlices.length > 0 && (
        <svg className={styles.svg}>
          {renderedSlices.map((item, index: number) => {
            return (
              <PieSlice
                cx={cx}
                cy={cy}
                itemIndex={index}
                id={focusedItemInfo.itemIndex === index ? activeId : undefined}
                isFocused={focusedItemInfo?.itemIndex === index && focusedItemInfo?.isFocusVisible}
                isHovered={hoveredItemInfo?.itemIndex === index}
                r={radius}
                startAngle={item.startAngle}
                gap={pieSliceGap}
                innerRadius={ir}
                angleExtent={item.angleExtent}
                color={item.color || colors[index % colors.length]}
                key={item.id}
                accessibleLabel={item.accessibleLabel}
              />
            );
          })}
        </svg>
      )}
      {props.children?.(getCenterContext(cx, cy, ir))}
      {datatipContent}
    </div>
  );
}
